import { useState, useEffect } from "react";
import { Clock, User, ChevronUp, ChevronDown } from "lucide-react";
import useOrder from "../hooks/useOrder";
import store from "../store";
import { observer } from "mobx-react-lite";
import { usePartnerOrders } from "../hooks/usePartnerOrders";
import VegIcon from "../assets/images/category-veg.svg";
import NonVegIcon from "../assets/images/category-nonveg.svg";

const itemStatus = {
  REJECTED: "rejected",
  REQUESTED: "requested",
  PREPARING: "preparing",
  SERVED: "served",
  CANCELLED: "cancelled",
};
const RunningKOTStatus = ({ open, onClose }) => {
  const [orders, setOrders] = useState([]);
  const [forceUpdate, setForceUpdate] = useState(0);
  const {
    orders: allOrders,
    processOrder,
    getOnlineOrderBillNo,
    cancelOrder,
    rejectOnlineOrder,
    processManualOrder,
    getAllOrders,
    generateOrderBill,
    completeOrderBill,
    flushAllCompleteOrders,
    setKOTItemPreparedStatus,
  } = useOrder();

  const { orders: partnersOrders } = usePartnerOrders();

  const fetchExistingOrder = async () => {
    const orders = await getAllOrders();
    setOrders(
      orders.map((order) => {
        const timeDifference = new Date() - new Date(order.createdAt); // Time difference in milliseconds
        const time = Math.floor(timeDifference / (1000 * 60));
        let tableDetails = store.rooms.reduce(
          (acc, room) => {
            const table = room.tables.find(
              (table) => table.qrpidVPA == order.tableVPA
            );
            if (table) {
              return {
                roomName: room.roomName,
                tableNo: table.tableNo,
              };
            }
            return acc;
          },
          {
            roomName: "",
            tableNo: "",
          }
        );

        order.items = order.items.filter(
          (item) => !item.canceled && item.status != itemStatus.REJECTED
        );
        return { ...order, time, table: tableDetails };
      })
    );
  };

  useEffect(() => {
    if (open) {
      fetchExistingOrder();
    }
  }, [open]);

  const handleItemClick = async (billNo, feUniqueItemId, order) => {
    console.log({ billNo, feUniqueItemId, order });
    const vpa = order.tableVPA;
    let status = "";

    const _orders = orders.map((order) =>
      order.billNo === billNo
        ? {
            ...order,
            items: order.items.map((item) => {
              if (item.feUniqueItemId === feUniqueItemId) {
                status =
                  item.status === itemStatus.PREPARING
                    ? itemStatus.SERVED
                    : itemStatus.PREPARING;

                return { ...item, status, updatedAt: new Date() };
              }
              return item;
            }),
          }
        : order
    );

    setOrders(_orders);
    await setKOTItemPreparedStatus({ vpa, billNo, feUniqueItemId, status });
  };

  // Close sidebar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const target = event.target;
      if (open && !target.closest('[data-sidebar="kot-sidebar"]')) {
        onClose();
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open, onClose]);

  // Close sidebar when pressing Escape key
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === "Escape" && open) {
        onClose();
      }
    };

    if (open) {
      document.addEventListener("keydown", handleEscKey);
    }

    return () => {
      document.removeEventListener("keydown", handleEscKey);
    };
  }, [open, onClose]);

  useEffect(() => {
    setForceUpdate((prev) => prev + 1);
  }, [partnersOrders]);

  return (
    <div className="" key={forceUpdate}>
      {/* Overlay */}
      {open && (
        <div className="fixed inset-0 bg-black/20 z-50" onClick={onClose} />
      )}

      {/* Sidebar */}
      <div
        data-sidebar="kot-sidebar"
        className={`fixed top-0 right-0 h-screen w-[80%] md:w-[450px] bg-white shadow-lg z-50 transform transition-transform duration-300 ease-in-out ${
          open ? "translate-x-0" : "translate-x-full"
        } overflow-y-auto hide-scrollbar`}
      >
        {/* Header */}
        <div className="p-4 border-b flex justify-between items-center">
          <div className="flex items-center gap-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect width="18" height="18" x="3" y="3" rx="2" />
              <path d="M3 9h18" />
              <path d="M9 21V9" />
            </svg>
            <span className="font-medium">Running KOTs</span>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
          </button>
        </div>

        {/* Orders */}
        <div className="p-4 space-y-4">
          {orders
            .sort((a, b) => a.billNo - b.billNo)
            .map((order) => (
              <OrderCard
                key={order.id}
                order={order}
                onItemClick={handleItemClick}
              />
            ))}
          {partnersOrders
            .filter((order) => order.orderStatus === "PREPARING")
            .map((order) => (
              <PartnerOrderCard key={order.id} order={order} />
            ))}
        </div>
      </div>
    </div>
  );
};

// Order Card components
function OrderCard({ order, onItemClick }) {
  const getOrderStatusColor = (status) => {
    let color = "bg-gray-300";
    switch (status) {
      case itemStatus.PREPARING:
        color = "bg-gray-300";
        break;
      case itemStatus.SERVED:
        color = "bg-green-300";
        break;
      case itemStatus.CANCELLED:
        color = "bg-red-300";
        break;
    }
    return color;
  };

  return (
    <div className="bg-blue-100 p-3 pt-4 rounded-md relative">
      <span className="text-xs absolute top-2 left-3">
        Room: {order.table.roomName} Table : {order.table.tableNo}
      </span>
      <span className="text-xs">Customer: {order.customer.name}</span>
      <div className="flex justify-between items-center mb-2 mt-1">
        <span className="font-medium">Bill : {order.billNo}</span>
        <div className="flex items-center text-sm text-gray-600">
          <span>{order.time} Mins</span>
        </div>
      </div>

      <div className="space-y-1">
        {order.items
          .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
          .map((item, index) => {
            const timeDifference =
              (item.status == itemStatus.SERVED
                ? new Date(item.updatedAt)
                : new Date()) - new Date(item.createdAt);
            const time = Math.floor(timeDifference / (1000 * 60));
            return (
              <div
                key={index}
                className={`flex justify-between items-center cursor-pointer hover:bg-blue-200 p-1 rounded ${
                  item.status == itemStatus.SERVED
                    ? "bg-green-500 text-white"
                    : ""
                }`}
                onClick={() =>
                  onItemClick(order.billNo, item.feUniqueItemId, order)
                }
              >
                <span className="text-sm">
                  {item.name} {item.isVariant && `- ${item.variantName}`}{" "}
                </span>
                <div className="flex items-center gap-3">
                  <span className="text-sm">Q: {item.quantity}</span>
                  <span className="text-sm">{time} Mins</span>
                  <div
                    className={`w-3 h-3 rounded-full ${getOrderStatusColor(
                      item.status
                    )}`}
                  ></div>
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
}

function PartnerOrderCard({ order }) {
  const formatTime = (timeString) => {
    return new Date(timeString).toLocaleString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const [isExpanded, setIsExpanded] = useState(true);
  const [prepTime, setPrepTime] = useState(30); // Default 30 minutes

  const toggleExpand = (e) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  const displayItems = isExpanded ? order.items : order.items.slice(0, 2);
  const hasMoreItems = order.items.length > 2;

  // const handleAccept = (e) => {
  //   e.stopPropagation();
  //   onAccept(order, prepTime);
  // };

  return (
    <div className="kanban-card">
      <div className="kanban-card-header">
        <div className="kanban-card-title">
          <span className="text-sm font-bold flex-1">
            Order #{order.orderId}
          </span>
          {order.orderOTP && order.orderOTP !== "NA" && (
            <span className="badge badge--warning ml-2">
              OTP: {order.orderOTP}
            </span>
          )}
          <div className="badge badge--neutral">{order.partnerName}</div>
        </div>
        <div className="kanban-card-time">{formatTime(order.createdAt)}</div>
      </div>

      <div className="kanban-card-content">
        <div className="kanban-card-items">
          {displayItems.map((item, index) => (
            <div key={index} className="kanban-card-item">
              <div className="flex items-center gap-2">
                {item.isVeg ? (
                  <div class="w-[15px] h-[15px] border border-green-600 rounded-sm flex items-center justify-center">
                    <div class="w-[8px] h-[8px] bg-green-600 rounded-full"></div>
                  </div>
                ) : (
                  <div class="w-[15px] h-[15px] border border-red-600 rounded-sm flex items-center justify-center">
                    <div class="w-[8px] h-[8px] bg-red-600 rounded-full"></div>
                  </div>
                )}
                <span className="flex-1 text-sm">{item.name}</span>
                <span className="text-sm font-medium">×{item.quantity}</span>
              </div>
            </div>
          ))}
          {hasMoreItems && (
            <button
              className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1 w-full justify-center py-1"
              onClick={toggleExpand}
            >
              {isExpanded ? (
                <>
                  <ChevronUp className="w-4 h-4" />
                  <span>Show less</span>
                </>
              ) : (
                <>
                  <ChevronDown className="w-4 h-4" />
                  <span>+{order.items.length - 2} more items</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* <div className="kanban-card-footer">
        <div className="kanban-card-actions">
          {order.orderStatus === "NEW" && (
            <>
              {isZomatoOrder && (
                <div className="flex items-center gap-2 mr-2">
                  <input
                    type="number"
                    min="1"
                    max="120"
                    value={prepTime}
                    onChange={(e) => setPrepTime(Number(e.target.value))}
                    className="w-16 h-8 px-2 border rounded text-sm"
                    placeholder="Prep time"
                  />
                  <span className="text-sm">min</span>
                </div>
              )}
              <button
                className="kanban-action-button kanban-action-button--primary"
                onClick={handleAccept}
                disabled={(isZomatoOrder && !prepTime) || loading}
              >
                <CheckCircle2 className="w-4 h-4" />
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  "Accept"
                )}
              </button>
            </>
          )}
          {order.orderStatus === "PREPARING" && (
            <button
              className="kanban-action-button kanban-action-button--success"
              onClick={(e) => {
                e.stopPropagation();
                onMarkReady(order);
              }}
              disabled={loading}
            >
              <Package className="w-4 h-4" />
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Ready"}
            </button>
          )}
          <button
            className="kanban-action-button kanban-action-button--warning"
            onClick={(e) => {
              e.stopPropagation();
              onViewDetails(order);
            }}
          >
            <MoveHorizontal className="w-4 h-4" />
            Details
          </button>
        </div>
      </div> */}
    </div>
  );
}

export default observer(RunningKOTStatus);
